import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCurrentStore } from '../../hooks/useCurrentStore';
import { useStoreInfoStore } from '../../stores/useStoreInfoStore';
import { Input, Label, FormField, FormError, CustomSelect } from '@redpanda/ui';
import OperatingHoursEditor, { OperatingHoursEditorRef } from '../../components/OperatingHoursEditor';
import ImageUploader from '../../components/ImageUploader';
import CapacitySettingsEditor, { CapacitySettingsEditorRef } from '../../components/CapacitySettingsEditor';
import { lineApi, BOOKING_BACKEND_URL } from '../../services/api';
import toast from 'react-hot-toast';

interface StoreFormData {
  name: string;
  type: string;
  address?: string;
  phone?: string;
  email?: string;
  logo?: string;
  coverImage?: string;
  slotInterval?: number; // 預約時段間隔
}

const SettingsPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'translation']);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<StoreFormData>();

  const { storeId, storeInfo, isLoading } = useCurrentStore();
  const { updateStoreInfo } = useStoreInfoStore();
  const [isSaving, setIsSaving] = useState(false);
  const operatingHoursRef = useRef<OperatingHoursEditorRef>(null);
  const capacityRef = useRef<CapacitySettingsEditorRef>(null);
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [coverImageUrl, setCoverImageUrl] = useState<string>('');

  // LINE 設定
  const [lineData, setLineData] = useState({
    lineChannelId: '',
    lineChannelSecret: '',
    lineAccessToken: '',
    lineEnabled: false,
    lineLoginChannelId: '',
    lineLoginChannelSecret: '',
    lineLiffId: '',
    lineLoginEnabled: false,
  });
  const [testUserId, setTestUserId] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  // 立即更新圖片路徑到資料庫
  const handleImageRemoved = async (field: 'logo' | 'coverImage') => {
    if (!storeId) return;

    // 更新本地狀態
    if (field === 'logo') {
      setLogoUrl('');
    } else {
      setCoverImageUrl('');
    }

    // 立即更新資料庫
    try {
      await updateStoreInfo(storeId, {
        [field]: null,
      });
    } catch (error) {
      console.error('更新圖片路徑失敗:', error);
    }
  };

  // 當店鋪資訊載入完成時，填入表單
  useEffect(() => {
    if (storeInfo) {
      reset({
        name: storeInfo.name || '',
        type: storeInfo.type || '',
        address: storeInfo.address || '',
        phone: storeInfo.phone || '',
        email: storeInfo.email || '',
        slotInterval: storeInfo.slotInterval || 30,
      });
      setLogoUrl(storeInfo.logo || '');
      setCoverImageUrl(storeInfo.coverImage || '');
      setLineData({
        lineChannelId: storeInfo.lineChannelId || '',
        lineChannelSecret: storeInfo.lineChannelSecret || '',
        lineAccessToken: storeInfo.lineAccessToken || '',
        lineEnabled: storeInfo.lineEnabled || false,
        lineLoginChannelId: storeInfo.lineLoginChannelId || '',
        lineLoginChannelSecret: storeInfo.lineLoginChannelSecret || '',
        lineLiffId: storeInfo.lineLiffId || '',
        lineLoginEnabled: storeInfo.lineLoginEnabled || false,
      });
    }
  }, [storeInfo, reset]);

  const onSubmit = async (data: StoreFormData) => {
    if (!storeId) return;

    try {
      setIsSaving(true);
      await Promise.all([
        updateStoreInfo(storeId, {
          ...data,
          logo: logoUrl || null,
          coverImage: coverImageUrl || null,
          ...lineData,
        }),
        operatingHoursRef.current?.save(),
        capacityRef.current?.save(),
      ]);
      toast.success(t('pages:admin.settings.saveSuccess'));
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || t('translation:common.error'));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 md:px-10 py-6">
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          {t('pages:admin.settings.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 md:px-10 py-6">
      <form id="settings-form" onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {t('pages:admin.settings.title')}
        </h1>
      </div>
      {/* 浮動儲存按鈕 */}
      <div className="fixed top-6 right-4 md:right-10 z-30">
        <button
          type="submit"
          disabled={isSaving}
          className={`font-bold py-2 px-4 rounded transition-all duration-200 ${
            isSaving
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-brand-600 hover:bg-brand-700 text-white'
          }`}
        >
          {isSaving ? t('pages:admin.settings.saving') : t('pages:admin.settings.saveChanges')}
        </button>
      </div>

      <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-4 md:p-6">
        <div className="space-y-6">
          {/* 店鋪名稱 */}
          <FormField>
            <Label htmlFor="name">
              {t('pages:admin.settings.storeName')} <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              autoComplete="organization"
              {...register('name', { required: t('pages:admin.settings.storeNameRequired') })}
              variant={errors.name ? 'error' : 'default'}
            />
            {errors.name && <FormError>{errors.name.message}</FormError>}
          </FormField>

          {/* 店鋪類型 */}
          <FormField>
            <Label htmlFor="type">
              {t('pages:admin.settings.storeType')} <span className="text-red-500">*</span>
            </Label>
            <Input
              id="type"
              autoComplete="off"
              {...register('type', { required: t('pages:admin.settings.storeTypeRequired') })}
              variant={errors.type ? 'error' : 'default'}
            />
            {errors.type && <FormError>{errors.type.message}</FormError>}
          </FormField>

          {/* 電話 */}
          <FormField>
            <Label htmlFor="phone">{t('pages:admin.settings.phone')}</Label>
            <Input
              id="phone"
              autoComplete="tel"
              {...register('phone')}
            />
          </FormField>

          {/* 店鋪 Logo */}
          {storeId && (
            <ImageUploader
              currentImageUrl={logoUrl}
              onImageUploaded={setLogoUrl}
              onImageRemoved={() => handleImageRemoved('logo')}
              folder="stores"
              label={t('pages:admin.settings.storeLogo')}
              helpText={t('pages:admin.settings.logoHelpText')}
            />
          )}

          {/* 封面圖片 */}
          {storeId && (
            <ImageUploader
              currentImageUrl={coverImageUrl}
              onImageUploaded={setCoverImageUrl}
              onImageRemoved={() => handleImageRemoved('coverImage')}
              folder="stores"
              label={t('pages:admin.settings.coverImage')}
              helpText={t('pages:admin.settings.coverHelpText')}
            />
          )}

          {/* Email */}
          <FormField>
            <Label htmlFor="email">{t('pages:admin.settings.email')}</Label>
            <Input
              type="email"
              id="email"
              autoComplete="email"
              {...register('email', {
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('pages:admin.settings.emailInvalid'),
                },
              })}
              variant={errors.email ? 'error' : 'default'}
            />
            {errors.email && <FormError>{errors.email.message}</FormError>}
          </FormField>

          {/* 地址 */}
          <FormField>
            <Label htmlFor="address">{t('pages:admin.settings.address')}</Label>
            <Input
              id="address"
              autoComplete="street-address"
              {...register('address')}
            />
          </FormField>

          {/* 預約時段間隔 */}
          <FormField>
            <Label htmlFor="slotInterval">
              {t('pages:admin.settings.slotInterval')}
            </Label>
            <CustomSelect
              id="slotInterval"
              name="slotInterval"
              value={String(watch('slotInterval') || 30)}
              onChange={(value) => setValue('slotInterval', Number(value))}
              options={[
                { value: '15', label: t('pages:admin.settings.slotIntervalOptions.15') },
                { value: '30', label: t('pages:admin.settings.slotIntervalOptions.30') },
                { value: '60', label: t('pages:admin.settings.slotIntervalOptions.60') },
                { value: '90', label: t('pages:admin.settings.slotIntervalOptions.90') },
                { value: '120', label: t('pages:admin.settings.slotIntervalOptions.120') },
              ]}
            />
          </FormField>

        </div>
      </div>

      {/* 營業時間設定 */}
      {storeId && (
        <div className="mt-6">
          <OperatingHoursEditor ref={operatingHoursRef} storeId={storeId} />
        </div>
      )}

      {/* 容量限制設定 */}
      {storeId && storeInfo && (
        <div className="mt-6">
          <CapacitySettingsEditor
            ref={capacityRef}
            storeId={storeId}
            initialDailyCapacity={storeInfo.dailyCapacity}
            initialMonthlyCapacity={storeInfo.monthlyCapacity}
          />
        </div>
      )}

      {/* LINE 整合 */}
      {storeId && (
        <div className="mt-6 bg-white dark:bg-slate-800 shadow rounded-lg p-4 md:p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('pages:admin.settings.lineIntegration.title')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {t('pages:admin.settings.lineIntegration.description')}
          </p>

          {/* LINE 推播通知 */}
          <div className="mb-8">
            <h3 className="text-md font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#06C755]"></span>
              {t('pages:admin.settings.lineIntegration.messagingApi')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="lineChannelId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Channel ID</label>
                <input
                  id="lineChannelId"
                  name="lineChannelId"
                  type="text"
                  autoComplete="off"
                  value={lineData.lineChannelId || ''}
                  onChange={(e) => setLineData({ ...lineData, lineChannelId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="lineChannelSecret" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Channel Secret</label>
                <input
                  id="lineChannelSecret"
                  name="lineChannelSecret"
                  type="password"
                  autoComplete="off"
                  value={lineData.lineChannelSecret || ''}
                  onChange={(e) => setLineData({ ...lineData, lineChannelSecret: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="lineAccessToken" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Channel Access Token</label>
                <input
                  id="lineAccessToken"
                  name="lineAccessToken"
                  type="password"
                  autoComplete="off"
                  value={lineData.lineAccessToken || ''}
                  onChange={(e) => setLineData({ ...lineData, lineAccessToken: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <label htmlFor="lineEnabled" className="flex items-center gap-2">
                <input
                  id="lineEnabled"
                  name="lineEnabled"
                  type="checkbox"
                  checked={lineData.lineEnabled || false}
                  onChange={(e) => setLineData({ ...lineData, lineEnabled: e.target.checked })}
                  className="h-4 w-4 text-[#06C755] rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {t('pages:admin.settings.lineIntegration.enableNotifications')}
                </span>
              </label>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={async () => {
                  if (!lineData.lineAccessToken) { toast.error(t('pages:admin.settings.lineIntegration.tokenInvalid')); return; }
                  setIsVerifying(true);
                  try {
                    const res = await lineApi.verifyToken(lineData.lineAccessToken);
                    toast[res.data.valid ? 'success' : 'error'](
                      t(res.data.valid ? 'pages:admin.settings.lineIntegration.tokenValid' : 'pages:admin.settings.lineIntegration.tokenInvalid')
                    );
                  } catch { toast.error(t('pages:admin.settings.lineIntegration.tokenInvalid')); }
                  setIsVerifying(false);
                }}
                disabled={isVerifying || !lineData.lineAccessToken}
                className="bg-brand-600 hover:bg-brand-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded transition-all duration-200"
              >
                {isVerifying ? '...' : t('pages:admin.settings.lineIntegration.verifyToken')}
              </button>
              <input
                id="testUserId"
                name="testUserId"
                type="text"
                autoComplete="off"
                value={testUserId}
                onChange={(e) => setTestUserId(e.target.value)}
                placeholder="U1234567890abcdef..."
                aria-label="LINE User ID"
                className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm w-48"
              />
              <button
                type="button"
                onClick={async () => {
                  if (!testUserId.trim()) { toast.error(t('pages:admin.settings.lineIntegration.testFailed')); return; }
                  setIsTesting(true);
                  try {
                    await lineApi.testPush(testUserId.trim());
                    toast.success(t('pages:admin.settings.lineIntegration.testSuccess'));
                  } catch (err: any) { toast.error(err.response?.data?.message || t('pages:admin.settings.lineIntegration.testFailed')); }
                  setIsTesting(false);
                }}
                disabled={isTesting || !lineData.lineAccessToken}
                className="font-bold py-2 px-4 rounded transition-all duration-200 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed text-white"
                style={{ backgroundColor: isTesting || !lineData.lineAccessToken ? undefined : '#06C755' }}
              >
                {isTesting ? t('pages:admin.settings.lineIntegration.testing') : t('pages:admin.settings.lineIntegration.testPush')}
              </button>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {t('pages:admin.settings.lineIntegration.userIdHint')}
            </p>
          </div>

          {/* LINE Login */}
          <div>
            <h3 className="text-md font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#06C755]"></span>
              {t('pages:admin.settings.lineIntegration.lineLogin')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              {t('pages:admin.settings.lineIntegration.lineLoginDescription')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="lineLoginChannelId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Login Channel ID</label>
                <input
                  id="lineLoginChannelId"
                  name="lineLoginChannelId"
                  type="text"
                  autoComplete="off"
                  value={lineData.lineLoginChannelId || ''}
                  onChange={(e) => setLineData({ ...lineData, lineLoginChannelId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="lineLoginChannelSecret" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Login Channel Secret</label>
                <input
                  id="lineLoginChannelSecret"
                  name="lineLoginChannelSecret"
                  type="password"
                  autoComplete="off"
                  value={lineData.lineLoginChannelSecret || ''}
                  onChange={(e) => setLineData({ ...lineData, lineLoginChannelSecret: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="lineLiffId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  LIFF ID <span className="text-gray-400 text-xs">({t('pages:admin.settings.lineIntegration.optional')})</span>
                </label>
                <input
                  id="lineLiffId"
                  name="lineLiffId"
                  type="text"
                  autoComplete="off"
                  value={lineData.lineLiffId || ''}
                  onChange={(e) => setLineData({ ...lineData, lineLiffId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <label htmlFor="lineLoginEnabled" className="flex items-center gap-2 mb-4">
              <input
                id="lineLoginEnabled"
                name="lineLoginEnabled"
                type="checkbox"
                checked={lineData.lineLoginEnabled || false}
                onChange={(e) => setLineData({ ...lineData, lineLoginEnabled: e.target.checked })}
                className="h-4 w-4 text-[#06C755] rounded"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {t('pages:admin.settings.lineIntegration.enableLineLogin')}
              </span>
            </label>

            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-md p-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('pages:admin.settings.lineIntegration.callbackUrl')}
              </label>
              <div className="flex items-center gap-2">
                <code className="text-sm text-gray-600 dark:text-gray-400 break-all flex-1">
                  {BOOKING_BACKEND_URL + '/api/line/auth/callback'}
                </code>
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(
                    BOOKING_BACKEND_URL + '/api/line/auth/callback'
                  )}
                  className="text-brand-600 hover:text-brand-700 text-sm whitespace-nowrap"
                >
                  {t('pages:admin.settings.lineIntegration.copyLink')}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {t('pages:admin.settings.lineIntegration.callbackUrlHint')}
              </p>
              <a
                href={lineData.lineLoginChannelId
                  ? `https://developers.line.biz/console/channel/${lineData.lineLoginChannelId}/line-login`
                  : 'https://developers.line.biz/console/'}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 text-sm text-brand-600 hover:text-brand-700 underline"
              >
                {t('pages:admin.settings.lineIntegration.openConsole')}
              </a>
            </div>
          </div>

        </div>
      )}

      {/* 店鋪 ID 顯示 */}
      {storeInfo && (
        <div className="mt-6 bg-gray-50 dark:bg-slate-900 rounded-lg p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('pages:admin.settings.storeId')}: <span className="font-mono font-semibold text-gray-900 dark:text-white break-all">{storeInfo.id}</span>
          </p>
        </div>
      )}
      </form>
    </div>
  );
};

export default SettingsPage;
